import { useMemo, useState } from 'react'

import {
  ADVERTISING_CHILD_LEVEL,
  ADVERTISING_LEVEL_LABELS,
  type AdvertisingLevel,
  type AdvertisingQuery,
  type AdvertisingReport,
  type AdvertisingRow,
} from '@/api/types'
import { TmtAttributedBadge } from '@/components/TmtAttributedBadge'
import { changeTone, formatChange, Icon, percentChange } from '@/design'

import { ProviderAttributedBadge } from './AdvertisingBadges'
import { childQuery, useAdvertisingReport } from './advertisingQuery'
import { advertisingErrorMessage } from './errors'
import { COLUMNS, formatMetric, TMT_METRIC_KEY, type ColumnDef } from './metrics'

const COLUMN_COUNT = COLUMNS.length + 2

function statusLabel(status: string | null, effectiveStatus: string | null): string {
  if (!status) return '—'
  if (effectiveStatus && effectiveStatus !== status) return `${status} (${effectiveStatus})`
  return status
}

/** Which attributed block `column` belongs to — `undefined` for a plain delivery
 * column (Spend, Impressions, …), which belongs to neither. Read off both the
 * `<th>`/`<td>` (`data-attribution`) and a DOM-based test. */
function attributionOf(column: ColumnDef): 'provider' | 'tmt' | undefined {
  if (column.providerAttributed) return 'provider'
  if (column.tmtAttributed) return 'tmt'
  return undefined
}

function currentValue(column: ColumnDef, row: AdvertisingRow): number | null {
  if (column.tmtAttributed) return row.tmt[TMT_METRIC_KEY[column.key]]
  return row.metrics[column.key]
}

function previousValue(column: ColumnDef, row: AdvertisingRow): number | null {
  if (column.tmtAttributed) return row.tmtPrevious ? row.tmtPrevious[TMT_METRIC_KEY[column.key]] : null
  return row.previous ? row.previous[column.key] : null
}

function MetricCell({ column, row, currency }: { column: ColumnDef; row: AdvertisingRow; currency: string }) {
  const value = currentValue(column, row)
  const hasPrevious = column.tmtAttributed ? row.tmtPrevious !== null : row.previous !== null
  const change = hasPrevious && value !== null ? percentChange(value, previousValue(column, row)) : null
  return (
    <span className="adv-metric">
      <span>{formatMetric(column.key, value, currency)}</span>
      {hasPrevious ? (
        <small className="adv-delta" data-tone={changeTone(change, 'up_good')} title="Compared with the previous period">
          {change === null ? '—' : formatChange(change)}
        </small>
      ) : null}
    </span>
  )
}

function RowGroup({
  row,
  level,
  baseQuery,
  currency,
  depth,
}: {
  row: AdvertisingRow
  level: AdvertisingLevel
  baseQuery: AdvertisingQuery
  currency: string
  depth: number
}) {
  const [expanded, setExpanded] = useState(false)
  const childLevel = ADVERTISING_CHILD_LEVEL[level]
  return (
    <>
      <tr>
        <th scope="row" style={{ paddingLeft: `${depth * 20}px` }}>
          {childLevel ? (
            <button
              type="button"
              className="adv-expand"
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
            >
              <Icon name="chevronDown" className={expanded ? 'adv-expand__icon' : 'adv-expand__icon adv-expand__icon--closed'} />
              <span>{row.name ?? row.id}</span>
            </button>
          ) : (
            <span className="adv-leaf-name">{row.name ?? row.id}</span>
          )}
        </th>
        <td>{statusLabel(row.status, row.effectiveStatus)}</td>
        {COLUMNS.map((column) => (
          <td
            key={column.key}
            data-align="end"
            data-attribution={attributionOf(column)}
            data-group-start={column.groupStart || undefined}
          >
            <MetricCell column={column} row={row} currency={currency} />
          </td>
        ))}
      </tr>
      {expanded && childLevel ? (
        <DrillDownRows level={childLevel} parentId={row.id} baseQuery={baseQuery} currency={currency} depth={depth + 1} />
      ) : null}
    </>
  )
}

/**
 * One expanded row's children, lazily fetched and cached by their own query
 * key so re-collapsing and re-expanding costs nothing on a warm cache.
 */
function DrillDownRows({
  level,
  parentId,
  baseQuery,
  currency,
  depth,
}: {
  level: AdvertisingLevel
  parentId: string
  baseQuery: AdvertisingQuery
  currency: string
  depth: number
}) {
  const query = useMemo(() => childQuery(baseQuery, level, parentId), [baseQuery, level, parentId])
  const result = useAdvertisingReport(query)

  if (result.isPending) {
    return (
      <tr>
        <td colSpan={COLUMN_COUNT} className="adv-child-state" style={{ paddingLeft: `${depth * 20}px` }}>
          Loading {ADVERTISING_LEVEL_LABELS[level].toLowerCase()}s…
        </td>
      </tr>
    )
  }
  if (!result.data) {
    return (
      <tr>
        <td colSpan={COLUMN_COUNT} role="alert" className="adv-child-state" style={{ paddingLeft: `${depth * 20}px` }}>
          {advertisingErrorMessage(result.error)}
        </td>
      </tr>
    )
  }
  if (result.data.rows.length === 0) {
    return (
      <tr>
        <td colSpan={COLUMN_COUNT} className="adv-child-state" style={{ paddingLeft: `${depth * 20}px` }}>
          No {ADVERTISING_LEVEL_LABELS[level].toLowerCase()}s in this period.
        </td>
      </tr>
    )
  }
  return (
    <>
      {result.data.rows.map((row) => (
        <RowGroup key={row.id} row={row} level={level} baseQuery={baseQuery} currency={currency} depth={depth} />
      ))}
      {result.hasNextPage ? (
        <tr>
          <td colSpan={COLUMN_COUNT} className="adv-child-state" style={{ paddingLeft: `${depth * 20}px` }}>
            <button
              type="button"
              className="ci-button"
              data-variant="ghost"
              disabled={result.isFetchingNextPage}
              onClick={result.fetchNextPage}
            >
              {result.isFetchingNextPage
                ? 'Loading more…'
                : `Load more ${ADVERTISING_LEVEL_LABELS[level].toLowerCase()}s`}
            </button>
          </td>
        </tr>
      ) : null}
    </>
  )
}

export function AdvertisingTable({
  report,
  baseQuery,
  sort,
  onSort,
}: {
  report: AdvertisingReport
  baseQuery: AdvertisingQuery
  sort: string
  onSort: (next: string) => void
}) {
  const field = sort.startsWith('-') ? sort.slice(1) : sort
  const descending = sort.startsWith('-')
  const toggle = (key: string) => onSort(field === key && descending ? key : `-${key}`)
  const sortIndicator = (key: string) =>
    field === key ? <Icon name={descending ? 'arrowDown' : 'arrowUp'} size={12} /> : <Icon name="sort" size={12} />

  return (
    <div className="ci-table-wrap" role="region" aria-label="Meta campaign report" tabIndex={0}>
      <table className="ci-table adv-table">
        <caption className="ci-visually-hidden">Meta campaign report</caption>
        <thead>
          <tr>
            <th scope="col">
              <button type="button" className="ci-table__sort" onClick={() => toggle('name')}>
                Campaign {sortIndicator('name')}
              </button>
            </th>
            <th scope="col">Status</th>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                data-align="end"
                data-attribution={attributionOf(column)}
                data-group-start={column.groupStart || undefined}
              >
                <button type="button" className="ci-table__sort" onClick={() => toggle(column.key)}>
                  {column.header} {sortIndicator(column.key)}
                </button>
                {column.providerAttributed ? <ProviderAttributedBadge attributionWindows={report.attributionWindows} /> : null}
                {column.tmtAttributed ? (
                  <TmtAttributedBadge model={report.tmtModel} lookbackDays={report.tmtLookbackDays} className="adv-tmt-badge" />
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row) => (
            <RowGroup key={row.id} row={row} level={report.level} baseQuery={baseQuery} currency={report.currency} depth={0} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function AdvertisingTotals({ report }: { report: AdvertisingReport }) {
  const totalsRow: AdvertisingRow = {
    id: 'totals',
    name: null,
    status: null,
    effectiveStatus: null,
    metrics: report.totals,
    previous: report.totalsPrevious,
    tmt: report.tmtTotals,
    tmtPrevious: report.tmtTotalsPrevious,
  }
  return (
    <dl className="adv-totals" aria-label="Totals">
      {COLUMNS.map((column) => (
        <div key={column.key} data-attribution={attributionOf(column)}>
          <dt>{column.header}</dt>
          <dd>
            <MetricCell column={column} row={totalsRow} currency={report.currency} />
          </dd>
        </div>
      ))}
    </dl>
  )
}
